/**
 * Utility functions for the ShipmentTimelineWithThreadLines component.
 */

import { apiClient } from "@/utils/apiClient";
import { EventLogger } from "../../utils/eventLogger";
import type {
  DownloadAttachmentRequest,
  DownloadAttachmentResponse,
} from "@services/shipments/types";

/**
 * Decode HTML entities in text.
 * @example decodeHtmlEntities("&amp;") // returns "&"
 */
export function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Extract email address from text like "Melike Kozan <melike@ajww.com>".
 * Returns the email portion, or the original text if no email found.
 */
export function extractEmail(text: string): string {
  const match = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return match ? match[0] : text;
}

/**
 * Download an email attachment via the API.
 * Creates a temporary link to trigger the browser download.
 */
export async function downloadAttachment(
  emailId: string,
  attachmentId: string,
  filename?: string,
): Promise<void> {
  try {
    const response = await apiClient.post<
      DownloadAttachmentResponse,
      DownloadAttachmentRequest
    >("/shipments/download-attachment", { emailId, attachmentId });

    const link = document.createElement("a");
    link.href = response.downloadUrl;
    link.download = response.filename || filename || "attachment";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    EventLogger.getInstance().track({
      name: "click",
      action: "download_attachment",
      attachmentName: response.filename || filename || "<unknown>",
    });
  } catch (err) {
    console.error("Download failed:", err);
  }
}
